import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  MessageCircle, 
  X, 
  Send, 
  Loader2,
  Sparkles,
  ChevronUp,
  ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format AI message with better styling
const FormattedMessage = ({ content }) => {
  const renderWithBold = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };
  
  const formatContent = (text) => {
    const paragraphs = text.split(/\n\n+/);
    
    return paragraphs.map((paragraph, pIndex) => {
      const lines = paragraph.split('\n');
      const isBulletList = lines.some(line => 
        line.trim().startsWith('•') || 
        line.trim().startsWith('-') || 
        (line.trim().startsWith('*') && !line.trim().startsWith('**'))
      );
      
      if (isBulletList) {
        return (
          <ul key={pIndex} className="space-y-1 my-2">
            {lines.map((line, lIndex) => {
              const trimmedLine = line.trim();
              if ((trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || (trimmedLine.startsWith('*') && !trimmedLine.startsWith('**')))) {
                return (
                  <li key={lIndex} className="flex items-start gap-2 text-xs">
                    <span className="text-tamba-teal mt-0.5 flex-shrink-0">•</span>
                    <span>{renderWithBold(trimmedLine.replace(/^[•\-\*]\s*/, ''))}</span>
                  </li>
                );
              }
              return line ? <p key={lIndex} className="text-xs">{renderWithBold(line)}</p> : null;
            })}
          </ul>
        );
      }
      
      const isNumberedList = lines.some(line => /^\d+[\.\)]\s/.test(line.trim()));
      
      if (isNumberedList) {
        return (
          <ol key={pIndex} className="space-y-1 my-2">
            {lines.map((line, lIndex) => {
              const trimmedLine = line.trim();
              const match = trimmedLine.match(/^(\d+)[\.\)]\s*(.*)/);
              if (match) {
                return (
                  <li key={lIndex} className="flex items-start gap-2 text-xs">
                    <span className="text-tamba-blue font-semibold min-w-[16px] flex-shrink-0">{match[1]}.</span>
                    <span>{renderWithBold(match[2])}</span>
                  </li>
                );
              }
              return line ? <p key={lIndex} className="text-xs">{renderWithBold(line)}</p> : null;
            })}
          </ol>
        );
      }
      
      return (
        <p key={pIndex} className="text-xs mb-2 last:mb-0 leading-relaxed">
          {renderWithBold(paragraph)}
        </p>
      );
    });
  };
  
  return <div className="space-y-1">{formatContent(content)}</div>;
};

const ContextualAIChat = ({ 
  context, // 'budget' | 'goals' | 'invest' | 'earn'
  userId,
  pageData = {},
  suggestedPrompts = [],
  accentColor = "tamba-blue"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const contextConfig = {
    budget: {
      title: "Budget Advisor",
      icon: "💰",
      placeholder: "Ask about your spending...",
      systemContext: "You are a budget advisor helping users understand their spending patterns and optimize their budget."
    },
    goals: {
      title: "Goals Coach",
      icon: "🎯",
      placeholder: "Ask about your goals...",
      systemContext: "You are a financial goals coach helping users set realistic goals and create action plans to achieve them."
    },
    invest: {
      title: "Investment Advisor",
      icon: "📈",
      placeholder: "Ask about investing...",
      systemContext: "You are an investment advisor helping users understand different investment options and strategies appropriate for their risk tolerance and goals."
    },
    earn: {
      title: "Earning Coach",
      icon: "💼",
      placeholder: "Ask about earning more...",
      systemContext: "You are a career and earning coach helping users identify opportunities to increase their income based on their skills and experience."
    }
  };

  const config = contextConfig[context] || contextConfig.budget;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const handleSend = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: messageText.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat/contextual`, {
        message: userMessage.content,
        user_id: userId,
        context: context,
        page_data: pageData
      });

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: response.data.response
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "I'm having trouble connecting. Please try again."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full bg-${accentColor} text-white shadow-lg shadow-${accentColor}/30 flex items-center justify-center`}
        data-testid={`${context}-chat-btn`}
      >
        <MessageCircle className="w-6 h-6" />
      </motion.button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      className="fixed bottom-24 right-6 z-40 w-80 sm:w-96"
    >
      <Card className="shadow-2xl border-border/50 overflow-hidden">
        {/* Header */}
        <div className={`bg-${accentColor} text-white p-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <span className="text-xl">{config.icon}</span>
            <div>
              <h3 className="font-semibold text-sm">{config.title}</h3>
              <p className="text-xs opacity-80">AI Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMinimized(!isMinimized)}
              className="h-8 w-8 text-white hover:bg-white/20"
            >
              {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 text-white hover:bg-white/20"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {!isMinimized && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
            >
              {/* Messages */}
              <ScrollArea className="h-64 p-3" ref={scrollRef}>
                {messages.length === 0 ? (
                  <div className="text-center py-4">
                    <Sparkles className={`w-8 h-8 mx-auto mb-2 text-${accentColor}`} />
                    <p className="text-sm text-muted-foreground mb-4">
                      How can I help you today?
                    </p>
                    {suggestedPrompts.length > 0 && (
                      <div className="space-y-2">
                        {suggestedPrompts.slice(0, 3).map((prompt, i) => (
                          <button
                            key={i}
                            onClick={() => handleSend(prompt)}
                            className={`w-full text-left text-xs p-2 rounded-lg border border-border hover:border-${accentColor}/50 hover:bg-${accentColor}/5 transition-colors`}
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div className={`max-w-[85%] p-2.5 rounded-xl ${
                          message.role === "user"
                            ? `bg-${accentColor} text-white rounded-br-sm`
                            : "bg-secondary rounded-bl-sm"
                        }`}>
                          {message.role === "user" ? (
                            <p className="text-xs">{message.content}</p>
                          ) : (
                            <FormattedMessage content={message.content} />
                          )}
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <div className="flex justify-start">
                        <div className="bg-secondary p-2.5 rounded-xl rounded-bl-sm">
                          <Loader2 className={`w-4 h-4 animate-spin text-${accentColor}`} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="p-3 border-t border-border/50">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={config.placeholder}
                    className="flex-1 h-9 text-sm rounded-full bg-secondary/50 border-0"
                    disabled={loading}
                  />
                  <Button
                    onClick={() => handleSend()}
                    disabled={!input.trim() || loading}
                    size="icon"
                    className={`h-9 w-9 rounded-full bg-${accentColor} hover:bg-${accentColor}/90`}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
};

export default ContextualAIChat;
